import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CIRCLE_PROMPT_CATEGORIES, type CirclePromptCategory } from "@connect/shared";
import { useApi } from "@/lib/api";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/button";

interface Prompt {
  id: string;
  category: string;
  body: string;
  ai_suggested: number;
  created_at: number;
}

interface Props {
  circleId: string;
  prompt: Prompt | null;
  canEdit: boolean;
  isMember: boolean;
  participationCount?: number;
}

/**
 * Guided prompt for a circle. Three layers, stacked top to bottom:
 *
 *   1. The active prompt itself (read-only display, or empty state)
 *   2. Leader/moderator publish controls (visible only when canEdit)
 *   3. Member's private response area (visible only when isMember and a
 *      prompt exists). Body is private — only the author can read it.
 *      The participation count visible to all members is a count only,
 *      never names.
 *
 * Tone-locked: no reactions, no likes, no reply threads, no "N members
 * are typing", no unread counter. Just a prompt, a private answer, and a
 * quiet count.
 */
export function GuidedPrompt({
  circleId,
  prompt,
  canEdit,
  isMember,
  participationCount,
}: Props) {
  const [editing, setEditing] = useState(false);

  if (!prompt) {
    return (
      <div className="panel p-8 animate-fade-up">
        <p className="label-eyebrow">Guided Prompt</p>
        <p className="mt-3 text-sm text-whisper leading-relaxed">
          Awaiting this week's guided prompt.
        </p>
        {canEdit && (
          <div className="mt-5">
            {editing ? (
              <PromptEditor
                circleId={circleId}
                onClose={() => setEditing(false)}
              />
            ) : (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-[10px] uppercase tracking-[0.18em] text-whisper hover:text-fog transition-colors"
              >
                Publish a prompt →
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <article className="panel p-8 animate-fade-up space-y-7">
      <div>
        <div className="flex items-start justify-between gap-6">
          <p className="label-eyebrow text-gold-glow">{prompt.category}</p>
          {prompt.ai_suggested === 1 && (
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted">
              AI-suggested
            </span>
          )}
        </div>
        <p className="mt-4 text-base text-fog leading-relaxed">{prompt.body}</p>
        {participationCount != null && participationCount > 0 && (
          <p className="mt-4 text-[10px] uppercase tracking-[0.18em] text-gold-glow/60">
            {participationCount === 1
              ? "1 member has reflected this week"
              : `${participationCount} members have reflected this week`}
          </p>
        )}
      </div>

      {isMember && (
        <div className="border-t border-veil/40 pt-6">
          <MemberResponseArea circleId={circleId} promptId={prompt.id} />
        </div>
      )}

      {canEdit && (
        <div className="border-t border-veil/40 pt-6">
          {editing ? (
            <PromptEditor
              circleId={circleId}
              initial={{ category: prompt.category as CirclePromptCategory, body: prompt.body }}
              onClose={() => setEditing(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-[10px] uppercase tracking-[0.18em] text-whisper hover:text-fog transition-colors"
            >
              Publish a new prompt →
            </button>
          )}
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Leader editor — publishes a new prompt (deactivates the old one server-side).
// ---------------------------------------------------------------------------

function PromptEditor({
  circleId,
  initial,
  onClose,
}: {
  circleId: string;
  initial?: { category: CirclePromptCategory; body: string };
  onClose: () => void;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const [category, setCategory] = useState<CirclePromptCategory>(
    initial?.category ?? "reflection",
  );
  const [body, setBody] = useState(initial?.body ?? "");

  const publish = useMutation({
    mutationFn: () =>
      api.post(`/circles/${circleId}/prompt`, { category, body: body.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["circle"] });
      qc.invalidateQueries({ queryKey: ["circle-prompts", circleId] });
      qc.invalidateQueries({ queryKey: ["circle-summary", circleId] });
      onClose();
    },
  });

  const trimmed = body.trim();
  const canPublish = trimmed.length >= 10 && !publish.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="label-eyebrow">
          {initial ? "Update the prompt" : "Publish a prompt"}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="text-[10px] uppercase tracking-[0.18em] text-whisper hover:text-fog"
        >
          Cancel
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {CIRCLE_PROMPT_CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCategory(c)}
            className={cn(
              "px-3 py-1.5 rounded-full text-xs transition-colors",
              c === category
                ? "border border-gold/40 text-gold-glow bg-veil/30"
                : "border border-veil/50 text-whisper hover:text-fog",
            )}
          >
            {c}
          </button>
        ))}
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
        placeholder="Write a quiet question worth sitting with this week."
        className="w-full bg-ink-elev/40 border border-veil/40 rounded-lg px-4 py-3 text-fog placeholder:text-muted leading-relaxed focus:outline-none focus:border-gold/40 resize-y"
      />
      <div className="flex items-center justify-between gap-4">
        <p className="text-[10px] uppercase tracking-[0.18em] text-muted">
          Replaces the active prompt. Previous prompt moves to the archive.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => publish.mutate()}
          disabled={!canPublish}
        >
          {publish.isPending ? "Publishing…" : initial ? "Update" : "Publish"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Member response area — private journaling tied to the active prompt.
// ---------------------------------------------------------------------------

function MemberResponseArea({
  circleId,
  promptId,
}: {
  circleId: string;
  promptId: string;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [hasEdited, setHasEdited] = useState(false);

  const existing = useQuery({
    queryKey: ["circle-prompt-response", promptId],
    queryFn: () =>
      api.get<{ response: { id: string; body: string; updated_at: number } | null }>(
        `/circles/${circleId}/prompts/${promptId}/my-response`,
      ),
  });

  const save = useMutation({
    mutationFn: (b: string) =>
      api.post(`/circles/${circleId}/prompts/${promptId}/respond`, { body: b }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["circle-prompt-response", promptId] });
      qc.invalidateQueries({ queryKey: ["circle-summary", circleId] });
      qc.invalidateQueries({ queryKey: ["circle-prompts", circleId] });
      setHasEdited(false);
    },
  });

  const stored = existing.data?.response;
  useEffect(() => {
    if (!hasEdited && stored?.body) setDraft(stored.body);
  }, [stored?.body, hasEdited]);

  const trimmed = draft.trim();
  const isUnchanged = trimmed === (stored?.body ?? "").trim();
  const canSave = trimmed.length > 0 && !isUnchanged && !save.isPending;

  return (
    <div>
      <p className="label-eyebrow">Your response</p>
      <p className="mt-1 text-xs text-whisper italic">
        Only you can read this. Your circle sees a count, never your words.
      </p>
      <textarea
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setHasEdited(true);
        }}
        rows={5}
        placeholder="Write whatever comes."
        className="mt-4 w-full bg-ink-elev/40 border border-veil/40 rounded-lg px-4 py-3 text-fog placeholder:text-muted leading-relaxed focus:outline-none focus:border-gold/40 resize-y"
      />
      <div className="mt-3 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.18em] text-muted">
          {stored
            ? `Saved · only you can see this`
            : "Only you can see this"}
        </p>
        <button
          type="button"
          onClick={() => save.mutate(trimmed)}
          disabled={!canSave}
          className="text-xs uppercase tracking-[0.18em] px-4 py-2 rounded-full border border-veil/60 text-fog hover:border-gold/50 hover:text-gold-glow transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {save.isPending
            ? "Saving…"
            : stored
              ? isUnchanged
                ? "Saved"
                : "Update"
              : "Save response"}
        </button>
      </div>
    </div>
  );
}
